import { useContext } from 'react';
import { QueryClient, useMutation } from '@tanstack/react-query';

import { TArchiveCommentReturn, TComment, TCreateCommentPayload, TCreateCommentReturn } from './types';

import { ApiContext } from '@shared/contexts';

import type { TItemType } from '@modules/items';
import type { TUseDocumentResult } from '@modules/documents';
import type { TUseFileResult } from '@modules/files';

// --- HELPER:

type TOperation = 'ADD' | 'UPDATE' | 'REMOVE';
export const updateCommentQueryData = (
  queryClient: QueryClient,
  itemId: string,
  itemType: TItemType,
  operation: TOperation,
  commentId: string,
  comment?: TComment
) => {
  const updateComments = (comments: TComment[]) => {
    if (operation === 'REMOVE') return comments.filter((c) => c._id !== commentId);

    if (!comment) {
      console.error(`Comment is missing for ${operation} operation!`);
      return comments;
    }
    if (operation === 'ADD') return [comment, ...comments];
    if (operation === 'UPDATE') return comments.map((c) => (c._id === commentId ? comment : c));
    return comments;
  };
  if (itemType === 'DOCUMENT') {
    return queryClient.setQueryData(
      [`document-${itemId}`],
      (oldData: TUseDocumentResult | undefined): TUseDocumentResult | undefined => {
        return oldData
          ? { ...oldData, doc: { ...oldData.doc, comments: updateComments(oldData.doc.comments) } }
          : oldData;
      }
    );
  }
  if (itemType === 'FILE')
    return queryClient.setQueryData(
      [`file-${itemId}`],
      (oldData: TUseFileResult | undefined): TUseFileResult | undefined =>
        oldData ? { ...oldData, file: { ...oldData.file, comments: updateComments(oldData.file.comments) } } : oldData
    );
};

// --- POST:

export const useCreateComment = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async ({ itemId, itemType, ...body }: TCreateCommentPayload): Promise<TCreateCommentReturn> =>
    await apiClient.post(`/comments/${itemId}/${itemType}`, body).then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      if (data.comment) {
        show('success', 'Added comment.');
        if (data.failedToAddSelection) show('warning', 'Added comment, but failed to add selection.');
        updateCommentQueryData(
          queryClient,
          variables.itemId,
          variables.itemType,
          'ADD',
          data.comment._id,
          data.comment
        );
      }
    },
    onError: handleApiError('Adding comment failed!'),
  });
};

export const useCreateReply = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async ({
    itemId,
    itemType,
    commentId,
    ...body
  }: {
    itemId: string;
    itemType: TItemType;
    commentId: string;
    content: string;
  }): Promise<TComment> =>
    await apiClient.post(`/comments/reply/${itemId}/${itemType}/${commentId}`, body).then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      show('success', 'Added reply.');
      updateCommentQueryData(queryClient, variables.itemId, variables.itemType, 'UPDATE', data._id, data);
    },
    onError: handleApiError('Adding reply failed!'),
  });
};

// --- PUT:

export const useUpdateContent = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async ({
    itemId,
    itemType,
    commentId,
    ...body
  }: {
    itemId: string;
    itemType: TItemType;
    commentId: string;
    replyId: string;
    content: string;
  }): Promise<TComment> =>
    await apiClient.put(`/comments/${itemId}/${itemType}/${commentId}`, body).then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      show('success', `Edited ${variables.replyId ? 'reply' : 'comment'}.`);
      updateCommentQueryData(queryClient, variables.itemId, variables.itemType, 'UPDATE', data._id, data);
    },
    onError: handleApiError('Editing comment failed!'),
  });
};

export const useUpdateResolution = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async ({
    itemId,
    itemType,
    commentId,
    resolved,
  }: {
    itemId: string;
    itemType: TItemType;
    commentId: string;
    resolved: boolean;
  }): Promise<TComment> =>
    await apiClient
      .put(`/comments/resolution/${itemId}/${itemType}/${commentId}?resolved=${resolved ? 'true' : 'false'}`)
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      show('success', variables.resolved ? 'Resolved comment.' : 'Unresolved comment.');
      updateCommentQueryData(queryClient, variables.itemId, variables.itemType, 'UPDATE', data._id, data);
    },
    onError: handleApiError('Changing resolution of comment failed!'),
  });
};

// --- DELETE:

export const useArchive = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async ({
    itemId,
    itemType,
    commentId,
    replyId,
    editingDraft,
  }: {
    itemId: string;
    itemType: TItemType;
    commentId: string;
    replyId?: string;
    editingDraft?: boolean;
  }): Promise<TArchiveCommentReturn> =>
    await apiClient
      .delete(
        `/comments/${itemId}/${itemType}/${commentId}?reply=${replyId || ''}&editingDraft=${editingDraft === true}`
      )
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      if (data && 'lockedUserId' in data) return;
      show('success', variables.replyId ? 'Deleted reply.' : 'Deleted comment.');
      updateCommentQueryData(
        queryClient,
        variables.itemId,
        variables.itemType,
        variables.replyId ? 'UPDATE' : 'REMOVE',
        variables.commentId,
        data
      );
    },
    onError: handleApiError('Deleting comment failed!'),
  });
};
